import ErrorPage from '@pages/other/ErrorPage/ErrorPage';
import MainLayout from '../layouts/MainLayout/MainLayout';
import paths from './paths';
import { permissionSets, RequirePermissions } from '@app/permissions';
import RedirectForbidden from '@app/permissions/RedirectForbidden';
import { RouteObject } from 'react-router-dom';
import { lazyComponent, lazyWrapComponent } from './lazy';

const mainRoutes: RouteObject[] = [
  {
    path: '/',
    element: <MainLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/forbidden',
        lazy: () =>
          lazyComponent(import('@pages/other/ForbiddenPage/ForbiddenPage')),
      },
      {
        path: '',
        lazy: () => lazyComponent(import('@pages/agent/InboxPage/InboxPage')),
      },
      {
        path: 'queues',
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(import('@pages/monitoring/QueuesPage/QueuesPage')),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.general.QUEUE_MONITORING}
                permissionsMatch='any'
                unavailable={<RedirectForbidden />}
              >
                {children}
              </RequirePermissions>
            ),
          ),
      },
      {
        path: 'conversations/:interactionId?',
        lazy: () =>
          lazyComponent(
            import('@pages/monitoring/ConversationsPage/ConversationsPage'),
          ),
      },
      {
        path: 'urgentfollowups/abandonedcalls/:abandonedCallFollowUpId?',
        lazy: () =>
          lazyComponent(
            import(
              '@pages/urgentfollowups/AbandonedCallsPage/AbandonedCallsPage'
            ),
          ),
      },
      {
        path: 'urgentfollowups/secondchanceleads/:secondChanceLeadFollowUpId?',
        lazy: () =>
          lazyComponent(
            import(
              '@pages/urgentfollowups/SecondChanceLeadsPage/SecondChanceLeadsPage'
            ),
          ),
      },
      {
        path: 'profile',
        lazy: () =>
          lazyComponent(import('@pages/agent/ProfilePage/ProfilePage')),
      },
      {
        path: 'debug',
        lazy: () => lazyComponent(import('@pages/other/DebugPage/DebugPage')),
      },
      {
        path: paths.settings,
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(import('@pages/settings/SettingsPage/SettingsPage')),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.admin.ALL}
                permissionsMatch='any'
                unavailable={<RedirectForbidden />}
              >
                {children}
              </RequirePermissions>
            ),
          ),
      },
      {
        path: `${paths.userManagement}/:userId?`,
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import('@pages/settings/UserManagementPage/UserManagementPage'),
            ),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.admin.USER_MANAGEMENT}
                permissionsMatch='any'
                unavailable={<RedirectForbidden />}
              >
                {children}
              </RequirePermissions>
            ),
          ),
      },
      {
        path: `${paths.workflows}/:workflowId?`,
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import('@pages/settings/WorkflowsPage/WorkflowManagementPage'),
            ),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.admin.TELECOM_WORKFLOWS}
                permissionsMatch='any'
                unavailable={<RedirectForbidden />}
              >
                {children}
              </RequirePermissions>
            ),
          ),
      },
      // Virtual Agents page
      {
        path: `${paths.virtualAgents}`,
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import(
                '@pages/settings/VirtualAgentsPage/VirtualAgentSettingsPage'
              ),
            ),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.admin.VIRTUAL_AGENT}
                permissionsMatch='any'
                unavailable={<RedirectForbidden />}
              >
                {children}
              </RequirePermissions>
            ),
          ),
      },

      // Manage Accounts page
      {
        path: `${paths.manageAccounts}`,
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import('@pages/settings/ManageAccountsPage/ManageAccountsPage'),
            ),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.admin.TELECOM_MANAGE_ACCOUNT}
                permissionsMatch='any'
                unavailable={<RedirectForbidden />}
              >
                {children}
              </RequirePermissions>
            ),
          ),
      },
      // Single Virtual Agents page
      {
        path: `${paths.virtualAgents}/:virtualAgentId`,
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import(
                '@pages/settings/VirtualAgentsPage/AccountVirtualAgentSettingsPage'
              ),
            ),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.admin.VIRTUAL_AGENT}
                permissionsMatch='any'
                unavailable={<RedirectForbidden />}
              >
                {children}
              </RequirePermissions>
            ),
          ),
      },
      {
        path: 'personalsettings',
        lazy: () =>
          lazyComponent(
            import('@pages/agent/PersonalSettingsPage/PersonalSettingsPage'),
          ),
      },
    ],
  },
];

export default mainRoutes;
